/**
 * Helpers de fecha en zona horaria LOCAL del sistema operativo.
 *
 * NUNCA usar `Date.toISOString().slice(0,10)` para obtener "hoy": ese método
 * devuelve UTC y después de las 19:00 en México (UTC-6 con DST) el resultado
 * es el día siguiente desde la perspectiva del operador.
 *
 * Válido tanto en Node.js (main process) como en el renderer — ambos usan
 * la TZ local del OS.
 */

/** Devuelve "YYYY-MM-DD" en hora local. */
export function toLocalYYYYMMDD(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Convierte un "YYYY-MM-DD" (el valor de un `<input type="date">`) al instante que representa
 * **ese día en hora local**, listo para mandarlo al servidor.
 *
 * Sprint 150 (DT-147) — Existe porque la pantalla de Reportes hacía `new Date("2026-09-20")`, y
 * eso **no** es el 20 de septiembre local: es medianoche UTC, o sea las 18:00 del 19 en México.
 * El servidor le aplicaba `startOfDay` y consultaba el día anterior. Cinco meses enseñando el día
 * equivocado, todos los días, sobre una pantalla de dinero.
 *
 * Se ancla al **mediodía** a propósito: es el único punto del día que sobrevive a cualquier huso
 * y a cualquier cambio de horario de verano sin cruzarse al día vecino.
 *
 * Devuelve `null` si el texto no tiene forma de fecha, para que quien llame decida —nunca un
 * `Invalid Date` viajando hacia la base.
 */
export function diaLocalDesdeTexto(texto: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(texto)) return null;
  const [a, m, d] = texto.split('-').map(Number);
  const fecha = new Date(a, m - 1, d, 12, 0, 0, 0);
  // Rechaza el 31 de febrero y compañía: `new Date` los corre al mes siguiente en silencio.
  if (fecha.getFullYear() !== a || fecha.getMonth() !== m - 1 || fecha.getDate() !== d) return null;
  return fecha;
}

/** Devuelve "YYYY-MM-DDTHH:MM" en hora local — compatible con input datetime-local. */
export function toLocalDateTimeLocal(d: Date): string {
  const date = toLocalYYYYMMDD(d);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${date}T${hh}:${mm}`;
}
